import {
  login as apiLogin,
  logoutUser as apiLogout,
  getCurrentUser,
  type UserResponse,
} from '@/services/authApi';
import { AuthContextType, LoginCredentials, Role, User } from '@/types/auth';
import { createContext, ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import { toast } from 'sonner';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const SESSION_FLAG = 'session';

function mapUserResponseToUser(res: UserResponse): User {
  const firstName = res.firstName ?? '';
  const middleName = res.middleName ?? undefined;
  const lastName = res.lastName ?? '';
  return {
    id: res._id ?? res.userId,
    email: res.userId,
    tenantId: res.tenantId ?? '',
    firstName,
    middleName: middleName || undefined,
    lastName,
    mobile: res.mobile ?? undefined,
    role: (res.role as Role) ?? 'role_user',
    status: res.registrationStatus === 'completed' ? 'active' : 'pending',
    referralCode: res.referralCode ?? undefined,
    createdAt: res.createdAt ?? new Date().toISOString(),
    updatedAt: res.updatedAt ?? new Date().toISOString(),
  };
}

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Restore session from cookie via GET /me
  useEffect(() => {
    let cancelled = false;
    getCurrentUser()
      .then((res) => {
        if (!cancelled) {
          setUser(mapUserResponseToUser(res));
          setToken(SESSION_FLAG);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setUser(null);
          setToken(null);
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(async (credentials: LoginCredentials) => {
    setIsLoading(true);
    try {
      const res = await apiLogin({
        email: credentials.email,
        password: credentials.password,
      });
      const mapped = mapUserResponseToUser(res);
      setUser(mapped);
      setToken(SESSION_FLAG);
      toast.success(`Welcome back, ${mapped.firstName || mapped.email}!`);
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : null;
      throw new Error(message || 'Invalid email or password');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const establishSession = useCallback((res: UserResponse) => {
    const mapped = mapUserResponseToUser(res);
    setUser(mapped);
    setToken(SESSION_FLAG);
    toast.success(`Welcome back, ${mapped.firstName || mapped.email}!`);
  }, []);

  const logout = useCallback(async () => {
    try {
      await apiLogout();
    } catch {
      // ignore
    }
    setToken(null);
    setUser(null);
    toast.success('Successfully logged out');
  }, []);

  const updateUser = useCallback((updatedUser: User) => {
    setUser(updatedUser);
  }, []);

  const hasRole = useCallback((roles: Role | Role[]): boolean => {
    if (!user) return false;
    const roleArray = Array.isArray(roles) ? roles : [roles];
    return roleArray.includes(user.role);
  }, [user]);

  const isAdmin = useCallback((): boolean => {
    return hasRole(['role_admin', 'role_superadmin']);
  }, [hasRole]);

  const value: AuthContextType = {
    user,
    token,
    isAuthenticated: !!token && !!user,
    isLoading,
    login,
    establishSession,
    logout,
    updateUser,
    hasRole,
    isAdmin,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export { AuthContext };
